import { CheckCircle2, AlertTriangle, Minus, Loader2 } from 'lucide-react';
import BarcodeScanner from '../shared/BarcodeScanner';
import type { CellState } from './OrderSheetGrid';
import type { OrderSheetRow } from '../../types/nursery';

/**
 * Scanning trees onto the order sheet: each scan adds one of whatever carries
 * that barcode, and the line saves as a typed quantity does. Built for a sale
 * across the counter — the customer is chosen, then their trees are scanned.
 *
 * The page does the adding; this shows what the last scans did, and lets one
 * be taken back off.
 */

/** What one scan did. */
export type ScanOutcome =
  | { kind: 'added'; row: OrderSheetRow; quantity: number }
  | { kind: 'unknown'; code: string }
  | { kind: 'refused'; code: string; message: string };

/** A scan's outcome as listed, newest first. */
export type ScanResult = ScanOutcome & { id: number };

export interface OrderScanDialogProps {
  results: ScanResult[];
  /** How each line's save is going, by row key: a refused save shows on its scan. */
  cells: Record<string, CellState>;
  /** No row on the sheet has a barcode yet. */
  noBarcodes: boolean;
  startManual: boolean;
  onScan: (code: string, source: 'camera' | 'manual') => void;
  onTakeOne: (row: OrderSheetRow) => void;
  onClose: () => void;
}

const rowName = (row: OrderSheetRow) =>
  [row.name, row.rootstock_name && `on ${row.rootstock_name}`].filter(Boolean).join(' ');

export function OrderScanDialog({
  results, cells, noBarcodes, startManual, onScan, onTakeOne, onClose,
}: OrderScanDialogProps) {
  // Only the newest scan of each line can take one off: an older one's count is stale.
  const newest = new Map<string, number>();
  for (const r of results) {
    if (r.kind === 'added' && !newest.has(r.row.key)) newest.set(r.row.key, r.id);
  }

  return (
    <BarcodeScanner label="Scan trees onto this order" onScan={onScan} onClose={onClose} startManual={startManual}>
      <div className="space-y-2" aria-live="polite">
        {noBarcodes && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 break-words">
            No stock item or product has a barcode yet. Add them on the stock item or product, then scan here.
          </p>
        )}
        {results.length === 0 ? (
          <p className="text-xs text-gray-500 break-words">
            Each scan adds one tree to the order. A USB scanner works too: choose Enter Manually and scan.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {results.map((r, i) => {
              const latest = i === 0;
              if (r.kind !== 'added') {
                return (
                  <li
                    key={r.id}
                    className={`flex items-start gap-2 text-xs rounded px-2 py-1.5 break-words ${latest ? 'bg-red-50 text-red-800' : 'text-gray-500'}`}
                  >
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span className="min-w-0">
                      {r.kind === 'unknown'
                        ? <>Nothing carries barcode <span className="font-mono break-all">{r.code}</span>. Nothing was added.</>
                        : r.message}
                    </span>
                  </li>
                );
              }
              const cell = cells[r.row.key];
              const failed = cell?.state === 'error';
              const saving = cell?.state === 'saving';
              const canTakeOne = newest.get(r.row.key) === r.id && r.quantity > 0;
              return (
                <li
                  key={r.id}
                  className={`flex items-start gap-2 text-xs rounded px-2 py-1.5 ${
                    failed ? 'bg-red-50 text-red-800' : latest ? 'bg-green-50 text-green-900' : 'text-gray-600'}`}
                >
                  {failed ? <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    : saving ? <Loader2 className="h-3.5 w-3.5 mt-0.5 shrink-0 animate-spin" />
                      : <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                  <span className="min-w-0 flex-1 break-words">
                    <span className="font-medium">{rowName(r.row)}</span>
                    {' '}({r.row.tree_type_name}). {r.quantity} on the order.
                    {failed && cell?.message && <span className="block">{cell.message}</span>}
                  </span>
                  {canTakeOne && (
                    <button
                      type="button"
                      onClick={() => onTakeOne(r.row)}
                      className="shrink-0 inline-flex items-center gap-0.5 text-blue-700 hover:text-blue-900 font-medium"
                      aria-label={`Take one ${rowName(r.row)} off`}
                    >
                      <Minus className="h-3 w-3" /> One
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </BarcodeScanner>
  );
}
